import React from 'react';
import { FormattedMessage } from 'react-intl-next';

import Button from '@atlaskit/button';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import ChevronDownIcon from '@atlaskit/icon/glyph/hipchat/chevron-down';
import EditorAddIcon from '@atlaskit/icon/glyph/editor/add';

import { useRightSidebarContext } from '@confluence/page-layout-context';
import {
	ISSUE_CREATE_SIDE_PANEL_ID,
	ISSUE_CREATE_SIDE_PANEL_WIDTH,
	useIssueCreateSidePanelContext,
	ISSUE_CREATE_WITH_AI_SIDE_PANEL_ID,
} from '@confluence/issue-create-side-panel';

import { createMultipleJiraIssuesHandle } from './builtInButtonHandlers';
import { AtlassianIntelligenceIcon } from './AtlassianIntelligenceIcon';

type CreateJiraIssueActionsProps = {
	activeSelection?: Selection | null;
	completeKey: string;
	container: any;
	contentId: string;
	lastModified?: string | null;
	showCreateMultipleIssuesButton: boolean;
	showAtlassianIntelligenceCreateButton: boolean;
};

export const CreateJiraIssueActions = ({
	activeSelection,
	completeKey,
	container,
	contentId,
	lastModified,
	showCreateMultipleIssuesButton,
	showAtlassianIntelligenceCreateButton,
}: CreateJiraIssueActionsProps) => {
	const rightSidebarContext = useRightSidebarContext();
	const [, actions] = useIssueCreateSidePanelContext();
	const shouldShowDropdown =
		showCreateMultipleIssuesButton || showAtlassianIntelligenceCreateButton;
	return shouldShowDropdown ? (
		<DropdownMenu
			testId="create-jira-issue-dropdown"
			shouldRenderToParent
			trigger={({ triggerRef, ...props }) => (
				<Button
					{...props}
					id="create-jira-issue-trigger"
					appearance="subtle"
					iconBefore={<EditorAddIcon size="medium" label="" />}
					iconAfter={<ChevronDownIcon size="medium" label="Drop down" />}
					ref={triggerRef}
				>
					<FormattedMessage
						id="highlight-actions.create-jira-issue"
						defaultMessage="Create Jira issue"
						description="Create a new Jira issue"
					/>
				</Button>
			)}
		>
			<DropdownItemGroup>
				<DropdownItem
					onClick={createSingleJiraIssueHandle(rightSidebarContext, actions, activeSelection)}
				>
					<FormattedMessage
						id="highlight-actions.single-issue"
						defaultMessage="Single issue"
						description="Create a single Jira issue"
					/>
				</DropdownItem>
				{showCreateMultipleIssuesButton && (
					<DropdownItem
						onClick={createMultipleJiraIssuesHandle(container, contentId, lastModified)}
					>
						<FormattedMessage
							id="highlight-actions.multiple-issues"
							defaultMessage="Multiple issues"
							description="Create multiple Jira issues from a table"
						/>
					</DropdownItem>
				)}
				{showAtlassianIntelligenceCreateButton && (
					<DropdownItem
						onClick={createSingleJiraIssueHandle(
							rightSidebarContext,
							actions,
							activeSelection,
							true,
						)}
						elemAfter={<AtlassianIntelligenceIcon size="large" />}
					>
						<FormattedMessage
							id="highlight-actions.use-atlassian-intelligence"
							defaultMessage="Create with Atlassian Intelligence"
							description="Create a single Jira issue using Atlassian Intelligence"
						/>
					</DropdownItem>
				)}
			</DropdownItemGroup>
		</DropdownMenu>
	) : (
		<Button
			id="create-jira-issue-button"
			appearance="subtle"
			iconBefore={<EditorAddIcon size="medium" label="" />}
			data-key={completeKey}
			onClick={createSingleJiraIssueHandle(rightSidebarContext, actions, activeSelection)}
		>
			<FormattedMessage
				id="highlight-actions.create-jira-issue"
				defaultMessage="Create Jira issue"
				description="Create a new Jira issue"
			/>
		</Button>
	);
};

const createSingleJiraIssueHandle =
	(rightSidebarContext, actions, activeSelection, isCreatedWithAi?: boolean) => () => {
		actions.setSummary(activeSelection?.toString() || '');
		isCreatedWithAi
			? rightSidebarContext.open(ISSUE_CREATE_WITH_AI_SIDE_PANEL_ID, ISSUE_CREATE_SIDE_PANEL_WIDTH)
			: rightSidebarContext.open(ISSUE_CREATE_SIDE_PANEL_ID, ISSUE_CREATE_SIDE_PANEL_WIDTH);
	};
